import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Power, Volume2, RotateCw } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

const effects = [
  {
    id: "chorus",
    name: "Chorus",
    description: "Creates rich, shimmering texture",
    min: 0,
    max: 100,
    default: 30,
    unit: "%"
  },
  {
    id: "phaser",
    name: "Phaser",
    description: "Sweeping filter effect",
    min: 0.1,
    max: 10,
    default: 2,
    unit: "Hz"
  },
  {
    id: "tremolo",
    name: "Tremolo",
    description: "Volume pulsing speed",
    min: 0.1,
    max: 20,
    default: 5,
    unit: "Hz"
  },
  {
    id: "delay",
    name: "Ping Pong Delay",
    description: "Stereo bounce timing",
    min: 0.05,
    max: 2,
    default: 0.25,
    unit: "s"
  },
  {
    id: "reverb",
    name: "Reverb",
    description: "Room ambiance depth",
    min: 0,
    max: 100,
    default: 30,
    unit: "%"
  },
  {
    id: "autopan",
    name: "Auto-Pan",
    description: "Left-right sound movement",
    min: 0.1,
    max: 10,
    default: 2,
    unit: "Hz"
  },
  {
    id: "spatial",
    name: "360° Spatial",
    description: "Immersive 3D sound rotation",
    min: 0.1,
    max: 10,
    default: 2,
    unit: "Hz"
  },
  {
    id: "flanger",
    name: "Flanger",
    description: "Metallic modulation effect",
    min: 0.1,
    max: 10,
    default: 0.5,
    unit: "Hz"
  }
];

const AudioEffects = ({ audioContext, masterGain, isPlaying }) => {
  const [activeEffects, setActiveEffects] = useState({});
  const [effectValues, setEffectValues] = useState(
    effects.reduce((acc, e) => ({ ...acc, [e.id]: e.default }), {})
  );
  const [effectNodes, setEffectNodes] = useState({});

  const createChorusEffect = useCallback(() => {
    if (!audioContext || !masterGain) return null;
    
    const delay1 = audioContext.createDelay();
    const delay2 = audioContext.createDelay();
    const delay3 = audioContext.createDelay();
    const lfo = audioContext.createOscillator();
    const lfoGain = audioContext.createGain();
    const wetGain = audioContext.createGain();
    const dryGain = audioContext.createGain();
    const output = audioContext.createGain();

    delay1.delayTime.value = 0.025;
    delay2.delayTime.value = 0.03;
    delay3.delayTime.value = 0.035;
    
    lfo.frequency.value = 0.5;
    lfoGain.gain.value = 0.002;
    
    const depth = effectValues.chorus / 100;
    wetGain.gain.value = depth * 0.5;
    dryGain.gain.value = 1 - depth * 0.3;

    lfo.connect(lfoGain);
    lfoGain.connect(delay1.delayTime);
    lfoGain.connect(delay2.delayTime);
    lfoGain.connect(delay3.delayTime);
    
    lfo.start();

    return { delay1, delay2, delay3, lfo, lfoGain, wetGain, dryGain, output };
  }, [audioContext, masterGain, effectValues.chorus]);

  const createPhaserEffect = useCallback(() => {
    if (!audioContext) return null;
    
    const filters = [];
    const lfo = audioContext.createOscillator();
    const lfoGain = audioContext.createGain();
    
    for (let i = 0; i < 4; i++) {
      const filter = audioContext.createBiquadFilter();
      filter.type = 'allpass';
      filter.frequency.value = 1000;
      filter.Q.value = 10;
      filters.push(filter);
    }
    
    lfo.frequency.value = effectValues.phaser;
    lfoGain.gain.value = 500;
    
    lfo.connect(lfoGain);
    filters.forEach(f => lfoGain.connect(f.frequency));
    lfo.start();

    return { filters, lfo, lfoGain };
  }, [audioContext, effectValues.phaser]);

  const createTremoloEffect = useCallback(() => {
    if (!audioContext) return null;
    
    const lfo = audioContext.createOscillator();
    const lfoGain = audioContext.createGain();
    const outputGain = audioContext.createGain();
    
    lfo.frequency.value = effectValues.tremolo;
    lfoGain.gain.value = 0.5;
    outputGain.gain.value = 1;
    
    lfo.connect(lfoGain);
    lfoGain.connect(outputGain.gain);
    lfo.start();

    return { lfo, lfoGain, outputGain };
  }, [audioContext, effectValues.tremolo]);

  const createAutoPanEffect = useCallback(() => {
    if (!audioContext) return null;
    
    const panner = audioContext.createStereoPanner();
    const lfo = audioContext.createOscillator();
    const lfoGain = audioContext.createGain();
    
    lfo.frequency.value = effectValues.autopan;
    lfoGain.gain.value = 1;
    
    lfo.connect(lfoGain);
    lfoGain.connect(panner.pan);
    lfo.start();

    return { panner, lfo, lfoGain };
  }, [audioContext, effectValues.autopan]);

  const createSpatialEffect = useCallback(() => {
    if (!audioContext) return null;
    
    const panner = audioContext.createPanner();
    panner.panningModel = 'HRTF';
    panner.distanceModel = 'inverse';
    panner.refDistance = 1;
    panner.maxDistance = 10000;
    panner.rolloffFactor = 1;
    
    let angle = 0;
    const radius = 2;
    
    const updatePosition = () => {
      if (!activeEffects.spatial) return;
      angle += effectValues.spatial * 0.02;
      panner.positionX.setValueAtTime(Math.sin(angle) * radius, audioContext.currentTime);
      panner.positionZ.setValueAtTime(Math.cos(angle) * radius, audioContext.currentTime);
      requestAnimationFrame(updatePosition);
    };
    
    if (activeEffects.spatial) {
      updatePosition();
    }

    return { panner };
  }, [audioContext, effectValues.spatial, activeEffects.spatial]);

  const toggleEffect = (effectId) => {
    setActiveEffects(prev => ({
      ...prev,
      [effectId]: !prev[effectId]
    }));
  };

  const updateEffectValue = (effectId, value) => {
    setEffectValues(prev => ({
      ...prev,
      [effectId]: value
    }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-300">Audio Effects</h4>
        <span className="text-xs text-gray-500">Speaker-Friendly + Headphones</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {effects.map((effect) => (
          <motion.div
            key={effect.id}
            whileHover={{ scale: 1.01 }}
            className={cn(
              "p-3 rounded-xl border transition-all duration-200",
              activeEffects[effect.id]
                ? "border-violet-500/50 bg-violet-500/10"
                : "border-white/10 bg-white/5"
            )}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => toggleEffect(effect.id)}
                  data-testid={`effect-${effect.id}-toggle`}
                  className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
                    activeEffects[effect.id]
                      ? "bg-violet-600 text-white"
                      : "bg-white/10 text-gray-500"
                  )}
                >
                  <Power size={14} />
                </motion.button>
                <div>
                  <span className="text-sm font-medium text-white">{effect.name}</span>
                  <span className={cn(
                    "ml-2 text-xs",
                    activeEffects[effect.id] ? "text-green-400" : "text-gray-600"
                  )}>
                    {activeEffects[effect.id] ? "ON" : "OFF"}
                  </span>
                </div>
              </div>
              <span className="text-xs text-violet-400 font-mono">
                {effectValues[effect.id]}{effect.unit}
              </span>
            </div>
            
            <p className="text-xs text-gray-500 mb-2">{effect.description}</p>
            
            <Slider
              value={[effectValues[effect.id]]}
              onValueChange={([v]) => updateEffectValue(effect.id, v)}
              min={effect.min}
              max={effect.max}
              step={(effect.max - effect.min) / 100}
              disabled={!activeEffects[effect.id]}
              data-testid={`effect-${effect.id}-slider`}
              className="py-1"
            />
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default AudioEffects;
